import { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from "react"

interface FormGroupWrapperProps {
    component: ReactNode
    label?: ReactNode
    required?: boolean
    hasError?: boolean
    hasWarning?: boolean
    hasSuccess?: boolean
    feedbackIconClassName?: string
    feedbackIconAriaLabel?: string
    feedbackIconStyle?: CSSProperties
    onFeedbackIconClick?: () => void
    hint?: ReactNode
    className?: string
    dataTestId?: string
}

const LabelWithRequiredIndicator = (props: { label: ReactNode, required: boolean }): ReactNode => {
    return <label>{props.label}{props.required ? "*" : ""}</label>
}

const FeedbackHint = (props: {
    show?: boolean
    hint?: ReactNode
    iconClassName: string
    iconAriaLabel?: string
    iconStyle?: CSSProperties
    onIconClick?: () => void
}): ReactNode => {
    const handleIconClick = (event: MouseEvent<HTMLSpanElement>): void => {
        event.preventDefault()
        props.onIconClick?.()
    }
    const handleIconKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            props.onIconClick?.()
        }
    }
    const feedbackIcon = props.show
        ? <span
            className={`form-control-feedback rioglyph ${props.iconClassName} ${props.onIconClick ? "cursor-pointer" : ""}`}
            style={props.onIconClick ? { ...props.iconStyle, pointerEvents: "auto" } : props.iconStyle}
            aria-hidden={props.onIconClick ? undefined : "true"}
            aria-label={props.iconAriaLabel}
            onClick={props.onIconClick ? handleIconClick : undefined}
            onKeyDown={props.onIconClick ? handleIconKeyDown : undefined}
            role={props.onIconClick ? "button" : undefined}
            tabIndex={props.onIconClick ? 0 : undefined}
        />
        : null
    const helpHint = props.show && props.hint
        ? <span className={"help-block"}><span>{props.hint}</span></span>
        : null
    return (
        <>
            {feedbackIcon}
            {helpHint}
        </>
    )
}

export const FormGroupWrapper = (props: FormGroupWrapperProps): ReactNode => {
    const { label, component, hasError, hasWarning, hasSuccess } = props

    if (!label) {
        return component
    }

    const feedbackClassName = hasError
        ? "has-error has-feedback"
        : hasWarning
            ? "has-warning has-feedback"
            : hasSuccess
                ? "has-success has-feedback"
                : ""
    const feedbackIconClassName = props.feedbackIconClassName
        ?? (hasSuccess ? "rioglyph-ok-sign" : "rioglyph-error-sign")
    const formWrapperClassName = `form-group ${
        feedbackClassName} ${props.className || ""}`

    return (
        <div className={formWrapperClassName} data-testid={props.dataTestId}>
            <LabelWithRequiredIndicator label={label} required={props.required ?? false} />
            {component}
            <FeedbackHint
                show={hasError || hasWarning || hasSuccess}
                hint={props.hint}
                iconClassName={feedbackIconClassName}
                iconAriaLabel={props.feedbackIconAriaLabel}
                iconStyle={props.feedbackIconStyle}
                onIconClick={props.onFeedbackIconClick}
            />
        </div>
    )
}
